import { useEffect, useState } from "react";
import { Input, Image, message, Select, Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import useUserStore from "../../../stores/store";
import {
  validateSupplier,
  prepareIngredientDropdowns,
  getPreviouseSelectedIngredientsId,
} from "../../../services/inventory/supplier.service";
import ApiService from "../../../utils/ApiService";
import CustomLoader from "../../menu/CustomLoader";

export default function SupplierAddEditComponent({ data = {} }) {
  const { restaurantId, user, showNotification, setNetworkCall } = useUserStore();
  const navigate = useNavigate();
  const [supplierId, setSupplierId] = useState(Object.keys(data).length > 0 ? data.id : "");
  const [supplierName, setSupplierName] = useState(Object.keys(data).length > 0 ? data.name : "");
  const [mobileNo, setMobileNo] = useState(Object.keys(data).length > 0 ? data.phone : "");
  const [address, setAddress] = useState(Object.keys(data).length > 0 ? data.address : "");
  const [supplierItems, setSupplierItems] = useState([
    {
      id: crypto.randomUUID(),
      itemName: "",
    },
  ]);
  const [ingredientDropdowns, setIngredientDropdowns] = useState([]);
  const [ingredientLoading, setIngredientLoading] = useState(false);
  const [selectedSupplierIngredients, setSelectedSupplierIngredients] = useState([]);

  function onHandleSupplierName(e) {
    const val = e.target.value;
    if (val.length > 100) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          You have reached your limit! Please make <span className="text-red-500">Supplier name</span> less than
          <span className="text-red-500"> 100 character</span>.
        </p>
      );
      return;
    }
    setSupplierName(val);
  }

  function onHandleMobileNo(e) {
    const val = e.target.value;

    if (val.length > 11) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          Please enter a valid <span className="text-red-500"> 11-digit </span>mobile number.
        </p>
      );
      return;
    }

    setMobileNo(val);
  }

  function onHandleAddress(e) {
    const val = e.target.value;

    if (val.length > 200) {
      message.warning(
        <p className="font-p_regular text-[12px]">
          You have reached your limit! Please make<span className="text-red-500"> address </span>less than
          <span className="text-red-500"> 200 character</span>.
        </p>
      );
      return;
    }

    setAddress(val);
  }

  async function supplierUpsert(payload) {
    const theUpsert = payload.hasOwnProperty("supplierId") && payload["supplierId"].length > 0 ? "updated" : "added";
    setNetworkCall(true);
    try {
      const res = await ApiService.INVENTORY_SUPPLIER_UPSERT(payload, user.token.token);
      if (res.code !== 200) {
        setNetworkCall(false);
        showNotification(res.data.message);
        return;
      }
      setNetworkCall(false);
      showNotification(`Supplier ${theUpsert} successfully.`, false);
      return navigate("/inventory/supplier");
    } catch (err) {
      setNetworkCall(false);
      showNotification(err.response.data.message);
      return;
    }
  }

  function onHandleCancel() {
    navigate("/inventory/supplier");
  }

  function onHandleSave() {
    if (!validateSupplier(supplierName, mobileNo, address, selectedSupplierIngredients)) return;

    const payload = {
      restaurantId,
      supplierId,
      name: supplierName.trim(),
      phone: mobileNo,
      address,
      ingredientIds: selectedSupplierIngredients,
      ingredientNames: [],
    };

    supplierUpsert(payload);
  }

  async function getIngredientItems() {
    const params = {
      restaurantId: restaurantId,
      supplierId: supplierId,
    };
    try {
      const res = await ApiService.GET_INVENTORY_INGREDIENT_DROPDOWN(params, user.token.token);
      if (res.code !== 200) {
        setIngredientLoading(false);
        showNotification(res.data.message);
        return;
      }
      const { payload } = res;
      const { records } = payload;
      // setIngredientItems(records);
      setIngredientLoading(false);
      return records;
    } catch (err) {
      showNotification(err.response.data.message);
      setIngredientLoading(false);
      return;
    }
  }

  function onHandleSupperIngredients(value) {
    setSelectedSupplierIngredients([...new Set(value)]);
  }

  useEffect(() => {
    async function fetchPrepareDropdowns() {
      const ingredientItems = await getIngredientItems();
      if (ingredientItems.length > 0) {
        setIngredientDropdowns(prepareIngredientDropdowns(ingredientItems));
        setSelectedSupplierIngredients(getPreviouseSelectedIngredientsId(ingredientItems));
      }
    }

    fetchPrepareDropdowns();

    return () => {
      setIngredientDropdowns([]);
    };
  }, [supplierId]);

  return (
    <section className="w-[442px] min-h-[615px] bg-white rounded-[12px]">
      <CustomLoader />
      <div className="px-5 py-[50px]">
        <div className="flex flex-col space-y-2">
          <label className="text-sm font-p_medium" htmlFor="supplierName">
            Supplier Name <span className="text-red-500">*</span>
          </label>
          <div className="w-full h-[48px]">
            <Input
              label="Supplier Name"
              value={supplierName}
              onChange={onHandleSupplierName}
              name="supplierName"
              placeholder="Name"
              className={"w-full h-full py-1.5 font-p_medium"}
            />
          </div>
        </div>

        <div className="flex flex-col space-y-2 mt-4">
          <label className="text-sm font-p_medium" htmlFor="mobileNo">
            Mobile No <span className="text-red-500">*</span>
          </label>
          <div className="w-full h-[48px]">
            <Input
              label="Supplier Name"
              type="number"
              value={mobileNo}
              onChange={onHandleMobileNo}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
              }}
              onWheel={(e) => e.target.blur()}   // 🔥 Prevent scroll increment
              name="mobileNo"
              placeholder="Mobile No"
              className={"w-full h-full py-1.5 font-p_medium no-spinner"}
            />
          </div>
        </div>

        <div className="flex flex-col space-y-2 mt-4">
          <label className="text-sm font-p_medium" htmlFor="mobileNo">
            Address <span className="text-red-500">*</span>
          </label>
          <div className="w-full h-[133px]">
            <textarea
              value={address}
              onChange={onHandleAddress}
              className="border border-px border-[#D5D8DB] rounded-[4px] w-full h-full indent-3"
            ></textarea>
          </div>
        </div>

        <div className="w-full flex flex-col mt-9 space-y-2">
          <label className="text-sm font-p_medium">Ingredients</label>
          {ingredientDropdowns.length > 0 ? (
            <div className="supplier-ingredient-wrapper">
              <Select
                className={"supplier-ingredients-select "}
                loading={ingredientLoading}
                mode="multiple"
                size={"middle"}
                placeholder="Please select"
                defaultValue={selectedSupplierIngredients}
                onChange={onHandleSupperIngredients}
                style={{ width: "100%", height: "48px" }}
                options={ingredientDropdowns}
                showSearch
                optionFilterProp="label"
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "").toLowerCase().localeCompare((optionB?.label ?? "").toLowerCase())
                }
              />
            </div>
          ) : (
            <div className="w-full flex flex-col mt-9 space-y-2">
              <div className="w-full h-[48px] supplier-skeleton-inputs mb-2">
                <Skeleton.Input active={true} size={"square"} block={true} />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-3 pb-[50px] px-5">
        <button
          onClick={onHandleCancel}
          className="font-bold w-[100px] h-[42px] bg-[#FCBD10] text-black rounded-[5px] text-base leading-[16px]"
        >
          Cancel
        </button>
        <button
          onClick={onHandleSave}
          className="font-bold w-[100px] h-[42px] bg-black text-white rounded-[5px] text-base leading-[16px]"
        >
          {supplierId ? "Update" : "Save"}
        </button>
      </div>
    </section>
  );
}
